import React, { useEffect, useState, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Server, Loader2, RefreshCw, CheckCircle, AlertCircle, ExternalLink, Clock, ArrowRight } from 'lucide-react';

const statusBadge = (license) => {
  if (license === 'active') return <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="cyclos-license-badge">LICENSE ACTIVE</Badge>;
  if (license === 'awaiting_credentials') return <Badge className="bg-sky-50 text-sky-700 border border-sky-200" data-testid="cyclos-license-badge">REACHABLE - NEEDS TOKEN</Badge>;
  if (license === 'unreachable') return <Badge className="bg-rose-50 text-rose-700 border border-rose-200" data-testid="cyclos-license-badge">UNREACHABLE</Badge>;
  if (license === 'pending_install') return <Badge className="bg-amber-50 text-amber-800 border border-amber-200" data-testid="cyclos-license-badge">AWAITING INSTALL</Badge>;
  return <Badge className="bg-rose-50 text-rose-700 border border-rose-200" data-testid="cyclos-license-badge">UNVERIFIED</Badge>;
};

const EMPTY_FORM = { type: '', subject: 'system', amount: 0, description: '', reference: '', source: 'EZYTRANSFER', custom_values: {} };

const CyclosLayer = () => {
  const [config, setConfig] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [fieldDefs, setFieldDefs] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [ledger, setLedger] = useState({ owner: 'system', account: '', owners: [], accounts: [], entries: [] });
  const [ledgerLoading, setLedgerLoading] = useState(false);

  const loadLedger = useCallback(async (owner, account) => {
    setLedgerLoading(true);
    try {
      const params = {};
      if (owner) params.owner = owner;
      if (account) params.account = account;
      const res = await api.get('/cyclos/ledger', { params });
      setLedger(res.data);
    } catch (e) {
      toast.error('Failed to load on-ledger activity');
    } finally {
      setLedgerLoading(false);
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, tx] = await Promise.all([
        api.get('/cyclos/config'),
        api.get('/cyclos/transactions'),
      ]);
      setConfig(cfg.data);
      setTxns(tx.data.local || []);
      if (cfg.data?.config?.configured) {
        const [acc, pt] = await Promise.allSettled([
          api.get('/cyclos/accounts'),
          api.get('/cyclos/payment-types'),
        ]);
        if (acc.status === 'fulfilled') setAccounts(acc.value.data.accounts || []);
        if (pt.status === 'fulfilled') setPaymentTypes(pt.value.data.payment_types || []);
        loadLedger();
      }
    } catch (e) {
      toast.error('Failed to load Cyclos status');
    } finally {
      setLoading(false);
    }
  }, [loadLedger]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const setField = (patch) => {
    setPreview(null);
    setForm((p) => ({ ...p, ...patch }));
  };

  const setCustomValue = (name, value) => {
    setPreview(null);
    setForm((p) => ({ ...p, custom_values: { ...p.custom_values, [name]: value } }));
  };

  const selectType = async (v) => {
    setPreview(null);
    setFieldDefs([]);
    setForm((p) => ({ ...p, type: v, custom_values: {} }));
    try {
      const res = await api.get('/cyclos/payment-fields', { params: { type: v } });
      const defs = res.data.fields || [];
      setFieldDefs(defs);
      const init = {};
      defs.forEach((f) => {
        init[f.internal_name] = /source/i.test(f.internal_name) ? 'EZYTRANSFER' : '';
      });
      setForm((p) => ({ ...p, custom_values: init }));
    } catch (e) {
      toast.error('Failed to load required fields for this payment type');
    }
  };

  const validate = () => {
    if (!form.type) { toast.error('Pick a Cyclos payment type'); return false; }
    if (!form.amount || form.amount <= 0) { toast.error('Enter an amount > 0'); return false; }
    const missing = fieldDefs.filter((f) => f.required && !(form.custom_values[f.internal_name] || '').trim());
    if (missing.length > 0) { toast.error(`Fill required field: ${missing[0].name}`); return false; }
    return true;
  };

  const previewPayment = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await api.post('/cyclos/payment', { ...form });
      setPreview(res.data.preview);
      toast.success('Preview OK — no funds moved. Review and execute.');
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Preview failed');
    } finally {
      setSubmitting(false);
    }
  };

  const executePayment = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await api.post('/cyclos/payment', { ...form, custom_values: preview?.custom_values || {}, confirm: 'EXECUTE-LIVE' });
      toast.success(`Payment executed — Cyclos tx ${res.data.transaction_number || res.data.reference}`);
      setForm(EMPTY_FORM);
      setPreview(null);
      loadAll();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Payment failed');
    } finally {
      setSubmitting(false);
    }
  };

  const downloadReceipt = async (id, reference) => {
    try {
      const res = await api.get(`/cyclos/receipt/${id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Cyclos_Confirmation_${reference || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to download receipt');
    }
  };

  const license = config?.license_status;
  const connected = config?.health?.connected;
  const lockedOut = config?.health?.status === 'locked_out';
  const blocking = config?.blocking_items || [];

  return (
    <div className="space-y-6" data-testid="cyclos-layer">
      {/* Connection & License */}
      <Card className="p-6" data-testid="cyclos-connection-card">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-1 h-8 bg-indigo-600 rounded-full" />
            <h3 className="text-xl font-semibold">Cyclos — Connection &amp; License</h3>
            {statusBadge(license)}
            {connected
              ? <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 gap-1"><CheckCircle className="w-3 h-3" />connected</Badge>
              : lockedOut
                ? <Badge className="bg-amber-50 text-amber-800 border border-amber-200 gap-1" data-testid="cyclos-lockout-badge"><Clock className="w-3 h-3" />temporarily locked</Badge>
                : <Badge className="bg-slate-100 text-slate-600 border border-slate-200 gap-1"><Clock className="w-3 h-3" />not connected</Badge>}
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={loadAll} data-testid="cyclos-refresh-btn" disabled={loading}>
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              <span className="ml-1">Refresh</span>
            </Button>
            <a href="https://license.cyclos.org/app/home" target="_blank" rel="noreferrer">
              <Button size="sm" variant="outline" data-testid="cyclos-portal-btn">
                <ExternalLink className="w-3.5 h-3.5 mr-1" /> License Portal
              </Button>
            </a>
          </div>
        </div>

        {lockedOut && (
          <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 mb-3" data-testid="cyclos-lockout-notice">
            <p className="text-xs text-amber-900">{config?.health?.message}</p>
          </div>
        )}

        {blocking.length > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 mb-3" data-testid="cyclos-blocking-items">
            <p className="text-xs font-semibold text-amber-800 mb-1 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" /> To go live, set these on the backend:
            </p>
            <ul className="list-disc pl-5 space-y-0.5">
              {blocking.map((b, i) => (
                <li key={i} className="text-[11px] text-amber-900 font-mono">{b}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
          <div className="border rounded-md p-3">
            <p className="text-[10px] uppercase tracking-wide text-slate-500">API URL</p>
            <p className="font-mono text-slate-800 break-all">{config?.config?.api_url || '— not set —'}</p>
          </div>
          <div className="border rounded-md p-3">
            <p className="text-[10px] uppercase tracking-wide text-slate-500">Access Client Token</p>
            <p className="font-mono text-slate-800">{config?.config?.access_client_token_set ? 'set ✓' : '— not set —'}</p>
          </div>
          <div className="border rounded-md p-3">
            <p className="text-[10px] uppercase tracking-wide text-slate-500">Owner / principal</p>
            <p className="font-mono text-slate-800">{config?.config?.owner || 'self'}</p>
          </div>
        </div>
        <p className="text-[11px] text-slate-500 mt-3">
          Cyclos is a self-hosted Java + PostgreSQL banking platform. EzyTransfer integrates with it over its REST API.
          Host Cyclos on your own server, then add the <span className="font-mono">CYCLOS_*</span> env vars to connect.
        </p>
      </Card>

      {/* Accounts / balances */}
      <Card className="p-6" data-testid="cyclos-accounts-card">
        <div className="flex items-center gap-3 mb-4">
          <Server className="w-5 h-5 text-indigo-600" />
          <h3 className="text-xl font-semibold">Accounts &amp; Balances</h3>
        </div>
        {!connected ? (
          <p className="text-sm text-slate-500" data-testid="cyclos-accounts-empty">
            Awaiting Cyclos connection — balances will appear here once the instance is reachable.
          </p>
        ) : accounts.length === 0 ? (
          <p className="text-sm text-slate-500">No accounts returned for owner <span className="font-mono">{config?.config?.owner}</span>.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {accounts.map((a, i) => (
              <div key={a.id || i} className="border rounded-lg p-3" data-testid="cyclos-account-card">
                <p className="text-xs text-slate-500">{a.type?.name || a.type?.id || 'Account'}</p>
                <p className="font-mono text-lg text-slate-900 mt-1">
                  {a.currency?.symbol || a.currency?.id || ''} {Number(a.status?.balance ?? a.balance ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* On-ledger activity — live from Cyclos */}
      <Card className="p-6" data-testid="cyclos-ledger-card">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-1 h-8 bg-indigo-600 rounded-full" />
            <h3 className="text-xl font-semibold">On-Ledger Activity</h3>
            <Badge className="bg-indigo-50 text-indigo-700 border border-indigo-200">live from Cyclos</Badge>
          </div>
          {connected && (ledger.owners || []).length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <div className="w-56">
                <Select value={ledger.owner} onValueChange={(v) => loadLedger(v)}>
                  <SelectTrigger data-testid="cyclos-ledger-owner-select"><SelectValue placeholder="Owner" /></SelectTrigger>
                  <SelectContent>
                    {ledger.owners.map((o) => (
                      <SelectItem key={o.key} value={o.key}>{o.display}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-72">
                <Select value={ledger.account} onValueChange={(v) => loadLedger(ledger.owner, v)}>
                  <SelectTrigger data-testid="cyclos-ledger-account-select"><SelectValue placeholder="Account" /></SelectTrigger>
                  <SelectContent>
                    {ledger.accounts.map((a) => (
                      <SelectItem key={a.internal_name} value={a.internal_name}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>
        {!connected ? (
          <p className="text-sm text-slate-500">Connect Cyclos to see live ledger activity.</p>
        ) : ledgerLoading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading ledger…</div>
        ) : ledger.entries.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="cyclos-ledger-empty">No ledger entries for this account yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="cyclos-ledger-table">
              <thead className="bg-slate-50 text-slate-600 text-[10px] uppercase tracking-wide">
                <tr>
                  <th className="text-left px-2 py-1.5">Date</th>
                  <th className="text-left px-2 py-1.5">Tx number</th>
                  <th className="text-left px-2 py-1.5">Payment type</th>
                  <th className="text-left px-2 py-1.5">Counterparty</th>
                  <th className="text-left px-2 py-1.5">Description</th>
                  <th className="text-right px-2 py-1.5">Amount (USD)</th>
                </tr>
              </thead>
              <tbody>
                {ledger.entries.map((e, i) => {
                  const neg = String(e.amount).startsWith('-');
                  return (
                    <tr key={`${e.transaction_number}-${i}`} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid="cyclos-ledger-row">
                      <td className="px-2 py-1.5 font-mono text-[10px]">{(e.date || '').slice(0, 16).replace('T', ' ')}</td>
                      <td className="px-2 py-1.5 font-mono text-[10px]">{e.transaction_number || '-'}</td>
                      <td className="px-2 py-1.5">{e.payment_type || '-'}</td>
                      <td className="px-2 py-1.5">
                        {e.counterparty || '-'}
                        {e.counterparty_user ? <span className="text-slate-500"> / {e.counterparty_user}</span> : null}
                      </td>
                      <td className="px-2 py-1.5 max-w-[280px] truncate" title={e.description || ''}>{e.description || '-'}</td>
                      <td className={`px-2 py-1.5 text-right font-mono ${neg ? 'text-rose-600' : 'text-emerald-700'}`}>
                        {Number(e.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Send Payment — Cyclos is the source */}
      <Card className="p-6" data-testid="cyclos-payment-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-1 h-8 bg-indigo-600 rounded-full" />
          <h3 className="text-xl font-semibold">Send Payment</h3>
          <Badge className="bg-indigo-50 text-indigo-700 border border-indigo-200">Cyclos source</Badge>
        </div>

        {!connected ? (
          <p className="text-sm text-slate-500" data-testid="cyclos-payment-disconnected">
            Connect Cyclos to send payments from its system accounts.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Payment type</Label>
                <Select value={form.type} onValueChange={selectType}>
                  <SelectTrigger data-testid="cyclos-payment-type-select"><SelectValue placeholder="Select a Cyclos payment type" /></SelectTrigger>
                  <SelectContent>
                    {paymentTypes.map((t) => (
                      <SelectItem key={t.internal_name || t.id} value={t.internal_name || t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Destination (owner / principal)</Label>
                <Input
                  data-testid="cyclos-subject-input"
                  placeholder='"system" or a username e.g. DHEEG26'
                  value={form.subject}
                  onChange={(e) => setField({ subject: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Amount (USD)</Label>
                <Input
                  data-testid="cyclos-amount-input"
                  type="number"
                  value={form.amount}
                  onChange={(e) => setField({ amount: parseFloat(e.target.value) || 0 })}
                />
              </div>
              {fieldDefs.map((f) => (
                <div className="space-y-2" key={f.internal_name}>
                  <Label>{f.name}{f.required ? ' *' : ''}</Label>
                  <Input
                    data-testid={`cyclos-cf-${f.internal_name}`}
                    placeholder={f.pattern ? `format: ${f.pattern}` : f.name}
                    value={form.custom_values[f.internal_name] || ''}
                    onChange={(e) => setCustomValue(f.internal_name, e.target.value)}
                  />
                </div>
              ))}
              <div className="space-y-2 md:col-span-2">
                <Label>Description</Label>
                <Input
                  data-testid="cyclos-description-input"
                  value={form.description}
                  onChange={(e) => setField({ description: e.target.value })}
                />
              </div>
            </div>

            {preview && (
              <div className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-3 mt-4" data-testid="cyclos-preview-panel">
                <p className="text-xs font-semibold text-indigo-800 mb-2">Preview — no funds moved yet</p>
                <div className="flex items-center gap-2 text-xs text-slate-800 flex-wrap">
                  <span className="font-mono">{preview.from_account || '—'} ({preview.from_number || '—'})</span>
                  <ArrowRight className="w-3.5 h-3.5 text-indigo-600" />
                  <span className="font-mono">{preview.to_account || '—'}{preview.to_user ? ` / ${preview.to_user}` : ''}</span>
                  <span className="ml-auto font-mono font-semibold">USD {Number(preview.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                {preview.custom_values && Object.keys(preview.custom_values).length > 0 && (
                  <div className="mt-2 space-y-0.5" data-testid="cyclos-preview-custom-values">
                    {Object.entries(preview.custom_values).map(([k, v]) => (
                      <p key={k} className="text-[10px] font-mono text-slate-600">{k}: {v}</p>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center gap-2 mt-4">
              <Button
                variant="outline"
                data-testid="cyclos-preview-btn"
                onClick={previewPayment}
                disabled={submitting}
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Preview (no funds move)
              </Button>
              <Button
                data-testid="cyclos-execute-btn"
                onClick={executePayment}
                disabled={submitting || !preview}
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Execute Payment
              </Button>
            </div>
            {!preview && (
              <p className="text-[11px] text-slate-500 mt-2">Run a preview first — Execute unlocks after a passing preview.</p>
            )}
          </>
        )}
      </Card>

      {/* Transactions */}
      <Card className="p-6" data-testid="cyclos-transactions-card">
        <h3 className="text-xl font-semibold mb-4">Cyclos Settlements</h3>
        {txns.length === 0 ? (
          <p className="text-sm text-slate-500">No Cyclos settlements yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="cyclos-transactions-table">
              <thead className="bg-slate-50 text-slate-600 text-[10px] uppercase tracking-wide">
                <tr>
                  <th className="text-left px-2 py-1.5">Date (UTC)</th>
                  <th className="text-left px-2 py-1.5">Reference</th>
                  <th className="text-right px-2 py-1.5">Amount</th>
                  <th className="text-left px-2 py-1.5">Source</th>
                  <th className="text-left px-2 py-1.5">Beneficiary</th>
                  <th className="text-left px-2 py-1.5">Status</th>
                  <th className="text-center px-2 py-1.5">Receipt</th>
                </tr>
              </thead>
              <tbody>
                {txns.map((t) => (
                  <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                    <td className="px-2 py-1.5 font-mono text-[10px]">{(t.created_at || '').slice(0, 19).replace('T', ' ')}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px] text-slate-500">{t.reference}</td>
                    <td className="px-2 py-1.5 text-right font-mono">${Number(t.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className="px-2 py-1.5">{t.source_account_key || t.source_account_name || 'Cyclos'}</td>
                    <td className="px-2 py-1.5">{t.cyclos_to || '-'}</td>
                    <td className="px-2 py-1.5">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] ${t.status === 'settled' ? 'bg-emerald-50 text-emerald-700' : t.status === 'cyclos_failed' ? 'bg-rose-50 text-rose-700' : 'bg-slate-50 text-slate-700'}`}>
                        {t.status}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <button
                        onClick={() => downloadReceipt(t.id, t.reference)}
                        data-testid={`cyclos-receipt-${t.id}`}
                        className="text-[10px] underline text-indigo-700 hover:text-indigo-900"
                      >
                        PDF
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default CyclosLayer;
